import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Pill,
  ShieldCheck,
  X,
} from 'lucide-react';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Patient } from '@/lib/types';
import { useClinic } from '@/contexts/ClinicContext';
import { medicationsService } from '@/services/medicationsService';
import { allergiesService } from '@/services/allergiesService';

interface RxSafetyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patient: Patient | null;
  prescriptionDraft?: {
    medication?: string;
    dose?: string;
    quantity?: string;
    refills?: string;
  };
}

type GateStatus = 'pass' | 'warn' | 'fail' | 'unknown';

interface GateCheck {
  id: string;
  label: string;
  status: GateStatus;
  detail: string;
}

/**
 * Sheet that runs deterministic safety gates against the patient's data and
 * the in-flight prescription draft. Replaces the rail-based safety panel.
 */
export default function RxSafetyDialog({
  open,
  onOpenChange,
  patient,
  prescriptionDraft,
}: RxSafetyDialogProps) {
  const { clinic } = useClinic();
  const [loading, setLoading] = useState(true);
  const [gates, setGates] = useState<GateCheck[]>([]);
  const [activeMedNames, setActiveMedNames] = useState<string[]>([]);
  const [allergyNames, setAllergyNames] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    if (!patient?.id || !clinic?.id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [meds, allergies] = await Promise.all([
          medicationsService.getPatientMedications(clinic.id, patient.id, {
            activeOnly: true,
          }),
          allergiesService.getActiveMedicationAllergyNames(
            clinic.id,
            patient.id,
          ),
        ]);
        if (cancelled) return;
        setActiveMedNames(
          (meds || [])
            .map((m) => (m.medication_display || '').toLowerCase())
            .filter(Boolean),
        );
        setAllergyNames(allergies.map((a: string) => a.toLowerCase()));
      } catch (err) {
        console.error('RxSafetyDialog data load failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, patient?.id, clinic?.id]);

  useEffect(() => {
    setGates(buildGates(prescriptionDraft, activeMedNames, allergyNames));
  }, [prescriptionDraft, activeMedNames, allergyNames]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side='right'
        className='flex w-full !max-w-md flex-col gap-0 rounded-l-2xl p-0 [&>button]:hidden'
      >
        {/* Header — single-line title + round close, mirrors AddPractitioner/SubscriptionDetail drawers */}
        <div className='flex items-center justify-between border-b border-slate-200 px-6 py-4'>
          <SheetTitle
            className='flex items-center gap-2 font-semibold text-slate-900'
            style={{ fontSize: '1.05rem' }}
          >
            <ShieldCheck className='h-5 w-5 text-teal-600' />
            Real-time Safety Check
          </SheetTitle>
          <SheetClose asChild>
            <button className='flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 transition-colors hover:bg-slate-50'>
              <X className='h-5 w-5 text-slate-600' />
            </button>
          </SheetClose>
        </div>

        {/* Body */}
        <div className='flex-1 space-y-4 overflow-y-auto px-6 py-5'>
          <SheetDescription className='text-sm text-slate-500'>
            IntelliHealth runs deterministic gates against the patient's record
            and the in-flight prescription.
          </SheetDescription>

          {prescriptionDraft?.medication && (
            <div className='rounded-md border border-slate-200 bg-slate-50 p-3'>
              <div className='flex items-center gap-1.5 text-sm font-semibold text-slate-900'>
                <Pill className='h-4 w-4 text-slate-500' />
                {prescriptionDraft.medication}
                {prescriptionDraft.dose ? ` — ${prescriptionDraft.dose}` : ''}
              </div>
              {(prescriptionDraft.quantity || prescriptionDraft.refills) && (
                <div className='mt-1.5 flex flex-wrap gap-1.5'>
                  {prescriptionDraft.quantity && (
                    <Badge variant='outline' className='text-xs'>
                      Qty {prescriptionDraft.quantity}
                    </Badge>
                  )}
                  {prescriptionDraft.refills && (
                    <Badge variant='outline' className='text-xs'>
                      Refills {prescriptionDraft.refills}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div className='flex items-center justify-center py-6 text-sm text-slate-500'>
              <Loader2 className='mr-2 h-4 w-4 animate-spin' /> Loading patient
              data…
            </div>
          ) : (
            <ul className='space-y-2'>
              {gates.map((g) => (
                <GateRow key={g.id} gate={g} />
              ))}
            </ul>
          )}

          <div className='rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800'>
            <span className='font-semibold'>Audit mode</span>
            <p className='mt-0.5 leading-relaxed'>
              Gates are advisory. Hard-stop enforcement is wired in the strategy
              roadmap; for now this surfaces the same data the backend evaluates.
            </p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function GateRow({ gate }: { gate: GateCheck }) {
  const tone =
    gate.status === 'fail'
      ? 'border-red-200 bg-red-50 text-red-700'
      : gate.status === 'warn'
      ? 'border-amber-200 bg-amber-50 text-amber-800'
      : gate.status === 'pass'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : 'border-slate-200 bg-slate-50 text-slate-600';

  const Icon =
    gate.status === 'pass'
      ? CheckCircle2
      : gate.status === 'warn' || gate.status === 'fail'
      ? AlertTriangle
      : ChevronRight;

  return (
    <li
      className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${tone}`}
    >
      <Icon className='mt-0.5 h-3.5 w-3.5 flex-shrink-0' />
      <div className='min-w-0 flex-1'>
        <div className='font-semibold'>{gate.label}</div>
        <div className='mt-0.5 leading-relaxed opacity-90'>{gate.detail}</div>
      </div>
    </li>
  );
}

function buildGates(
  draft: RxSafetyDialogProps['prescriptionDraft'],
  activeMeds: string[],
  allergies: string[],
): GateCheck[] {
  const med = (draft?.medication || '').toLowerCase().trim();
  const checks: GateCheck[] = [];

  if (med && allergies.length > 0) {
    const hit = allergies.find(
      (a) => med.includes(a) || a.includes(med.split(' ')[0]),
    );
    checks.push({
      id: 'allergy',
      label: 'Allergy Contraindication',
      status: hit ? 'fail' : 'pass',
      detail: hit
        ? `Patient allergic to "${hit}" — review before prescribing.`
        : 'No active medication allergies match this drug.',
    });
  } else {
    checks.push({
      id: 'allergy',
      label: 'Allergy Contraindication',
      status: med ? 'pass' : 'unknown',
      detail: med
        ? 'No active medication allergies on file.'
        : 'No medication selected.',
    });
  }

  if (med && activeMeds.length > 0) {
    const dup = activeMeds.find((m) => m.includes(med) || med.includes(m));
    checks.push({
      id: 'duplicate',
      label: 'Duplicate Therapy',
      status: dup ? 'warn' : 'pass',
      detail: dup
        ? `Patient already on "${dup}". Confirm whether this is a refill or new order.`
        : 'No duplicate active medications detected.',
    });
  } else {
    checks.push({
      id: 'duplicate',
      label: 'Duplicate Therapy',
      status: 'unknown',
      detail: 'No medication selected.',
    });
  }

  checks.push({
    id: 'interactions',
    label: 'Drug Interactions',
    status: 'unknown',
    detail:
      activeMeds.length > 0
        ? `${activeMeds.length} active medication(s) on file. Drug Data Service integration pending.`
        : 'No active medications to compare against.',
  });

  checks.push({
    id: 'lab-recency',
    label: 'Lab Recency',
    status: 'unknown',
    detail:
      'Backend lab-recency gate not yet wired. Verify last lab date on the Labs tab.',
  });

  if (med) {
    const isTrt = /testosterone/.test(med);
    const isGlp1 =
      /semaglutide|tirzepatide|liraglutide|ozempic|wegovy|mounjaro/.test(med);
    if (isTrt) {
      checks.push({
        id: 'trt-90',
        label: 'TRT 90-Day Refill Gate',
        status: 'unknown',
        detail:
          'TRT controlled-substance monitoring required every 90 days. Verify lab interval.',
      });
    }
    if (isGlp1) {
      checks.push({
        id: 'glp1-120',
        label: 'GLP-1 120-Day Refill Gate',
        status: 'unknown',
        detail:
          'GLP-1 monitoring required every 120 days. Verify lab interval and titration milestone.',
      });
    }
  }

  return checks;
}
